// @flow
import {ActionCreator, createReducer} from '../../../store/utils';
import update from 'immutability-helper';
import {ofType, StateObservable} from 'redux-observable';
import {of, forkJoin} from 'rxjs';
import {switchMap, map, concatMap, catchError} from 'rxjs/operators';

import {saveStoreInformationApi} from './api';
import {uploadImageStoreApi} from '../../file-storage/api';
import {createSingleFieldFormData} from '../../../utils/form';

import type {Action} from '../../../store/utils';
import type {RootState} from '../../index';
import type {ActionsObservable} from 'redux-observable';
import type {StoredFile} from '../../../entities';
import type {SaveInformationRequest} from './api';
import type {ApiError} from '../../../http/error';

export type MyStoreAction = Action & {};
export type MyStoreState = {
  isSaving?: boolean,
  error?: string,
  saveStoreInformationSuccess?: boolean,
};

export class SaveStoreInformation extends ActionCreator<MyStoreAction> {}
export class SaveStoreInformationSuccess extends ActionCreator<MyStoreAction> {}
export class SaveStoreInformationError extends ActionCreator<MyStoreAction> {}

export const actions = {
  saveStoreInformation: (payload: MyStoreAction) =>
    new SaveStoreInformation(payload),
};

export const initialState: MyStoreState = {};

export default createReducer<MyStoreState>(initialState, {
  [SaveStoreInformation.type]: (
    state: MyStoreState,
    action: MyStoreAction
  ) => ({
    isSaving: true,
    saveStoreInformationSuccess: undefined,
    error: undefined,
  }),
  [SaveStoreInformationSuccess.type]: (
    state: MyStoreState,
    action: MyStoreAction
  ) => ({
    isSaving: undefined,
    saveStoreInformationSuccess: true,
    error: undefined,
  }),
  [SaveStoreInformationError.type]: (
    state: MyStoreState,
    action: MyStoreAction
  ) => ({
    isSaving: undefined,
    saveStoreInformationSuccess: false,
    error: action.error,
  }),
});

export const saveStoreInformationEpic = (
  action$: ActionsObservable<MyStoreAction>,
  state$: StateObservable<RootState>
) =>
  action$.pipe(
    ofType(SaveStoreInformation.type),
    switchMap((action: MyStoreAction) => {
      const {storeInformationRequest}: {storeInformationRequest: any} = action;

      const uploads = [];

      if (storeInformationRequest.profileImageFile) {
        const fd = createSingleFieldFormData(
          storeInformationRequest.profileImageFile,
          'file',
          {filePrefix: 'ui/store-information'}
        );
        uploads.push(
          uploadImageStoreApi(fd).pipe(
            map((files: Array<StoredFile>) => ({
              field: 'profileImage',
              file: files[0],
            }))
          )
        );
      }

      if (storeInformationRequest.coverImageFile) {
        const fd = createSingleFieldFormData(
          storeInformationRequest.coverImageFile,
          'file',
          {filePrefix: 'ui/store-information'}
        );
        uploads.push(
          uploadImageStoreApi(fd).pipe(
            map((files: Array<StoredFile>) => ({
              field: 'cover',
              file: files[0],
            }))
          )
        );
      }

      if (storeInformationRequest.logoImageFile) {
        const fd = createSingleFieldFormData(
          storeInformationRequest.logoImageFile,
          'file',
          {filePrefix: 'ui/store-information'}
        );
        uploads.push(
          uploadImageStoreApi(fd).pipe(
            map((files: Array<StoredFile>) => ({
              field: 'logo',
              file: files[0],
            }))
          )
        );
      }

      if (storeInformationRequest.faviconFile) {
        const fd = createSingleFieldFormData(
          storeInformationRequest.faviconFile,
          'file',
          {filePrefix: 'ui/store-information'}
        );
        uploads.push(
          uploadImageStoreApi(fd).pipe(
            map((files: Array<StoredFile>) => ({
              field: 'favicon',
              file: files[0],
            }))
          )
        );
      }

      if (uploads.length === 0) {
        return of([[], storeInformationRequest, null]);
      }

      return forkJoin(uploads).pipe(
        map((results: Array<{field: string, file: StoredFile}>) => [
          results,
          storeInformationRequest,
          null,
        ]),
        catchError((error: ApiError) => of([null, null, error]))
      );
    }),
    concatMap(
      ([results, storeInformationRequest, error]: [
        ?Array<{field: string, file: StoredFile}>,
        ?SaveInformationRequest,
        ?ApiError
      ]) => {
        if (error) {
          return of(new SaveStoreInformationError({error}));
        }

        if (results && storeInformationRequest) {
          results.forEach(({field, file}) => {
            switch (field) {
              case 'profileImage':
                storeInformationRequest = {
                  ...storeInformationRequest,
                  profileInformation: {
                    ...(storeInformationRequest?.profileInformation || {}),
                    profilePhotoId: file.id,
                  },
                };
                break;
              case 'cover':
                storeInformationRequest = {
                  ...storeInformationRequest,
                  coverId: file.id,
                };
                break;
              case 'logo':
                storeInformationRequest = {
                  ...storeInformationRequest,
                  logoId: file.id,
                };
                break;
              case 'favicon':
                storeInformationRequest = {
                  ...storeInformationRequest,
                  faviconId: file.id,
                };
                break;
              default:
                console.warn(`Unknown field: ${field}`);
            }
          });
        }

        const cleanedRequest = update(storeInformationRequest, {
          $unset: [
            'profileImageFile',
            'coverImageFile',
            'logoImageFile',
            'faviconFile',
          ],
        });

        return saveStoreInformationApi(cleanedRequest).pipe(
          map(data => new SaveStoreInformationSuccess({data})),
          catchError((apiError: ApiError) =>
            of(new SaveStoreInformationError({error: apiError}))
          )
        );
      }
    )
  );
